import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const RoleContext = createContext({
    role: null,
    permissions: {},
    loading: true,
    isSuperAdmin: false,
    isEditor: false,
    isStoreManager: false,
    isPM: false,
    isClient: false,
    isEstimator: false,
    isArchEstimator: false,
    isConstrEstimator: false,
    isAnyEstimator: false,
});

export const RoleProvider = ({ children, session }) => {
    const [role, setRole] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!session?.user) {
            setRole(null);
            setPermissions({});
            setLoading(false);
            return;
        }

        const fetchAuth = async () => {
            setLoading(true);
            try {
                const [{ data: userRole, error: roleErr }, { data: perms, error: permErr }] = await Promise.all([
                    supabase.rpc('get_user_role'),
                    supabase.rpc('get_user_permissions'),
                ]);

                if (roleErr) console.error('Error fetching role:', roleErr);
                if (permErr) console.error('Error fetching permissions:', permErr);

                setRole(userRole || 'viewer');
                setPermissions(perms || {});
            } catch (err) {
                console.error('Unexpected auth error:', err);
                setRole('viewer');
                setPermissions({});
            } finally {
                setLoading(false);
            }
        };

        fetchAuth();
    }, [session]);

    const isSuperAdmin      = role === 'super_admin';
    const isEditor           = isSuperAdmin || !!permissions['admin.blog.access'] || !!permissions['admin.portfolios.access'] || !!permissions['admin.monday.access'];
    const isStoreManager     = isSuperAdmin || !!permissions['admin.products.access'];
    const isPM               = isSuperAdmin || !!permissions['admin.workflow.access'] || !!permissions['admin.estimator.access'];
    const isClient           = role === 'client';
    const isEstimator        = isSuperAdmin || !!permissions['admin.estimator.access'];
    const isArchEstimator    = !!permissions['admin.estimator.architecture'];
    const isConstrEstimator  = !!permissions['admin.estimator.construction'];
    const isAnyEstimator     = isEstimator || isArchEstimator || isConstrEstimator;

    return (
        <RoleContext.Provider value={{ role, permissions, loading, isSuperAdmin, isEditor, isStoreManager, isPM, isClient, isEstimator, isArchEstimator, isConstrEstimator, isAnyEstimator }}>
            {children}
        </RoleContext.Provider>
    );
};

export const useRole = () => useContext(RoleContext);
